import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { toast } from 'react-hot-toast';

const CategoryCityMapping = ({ categories, cities, mappings, setMappings }) => {
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('category');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [newMapping, setNewMapping] = useState({ categoryId: '', cityId: '', status: 'active' });

  // Get mapping details with category and city names
  const getMappingDetails = () => {
    return mappings?.map(mapping => ({
      ...mapping,
      category: categories?.find(cat => cat?.id === mapping?.categoryId),
      city: cities?.find(city => city?.id === mapping?.cityId)
    }))?.filter(mapping => mapping?.category && mapping?.city);
  };

  // Filter mappings
  useEffect(() => {
    let filtered = getMappingDetails();

    // Apply filters
    if (filterStatus !== 'all') {
      filtered = filtered?.filter(mapping => mapping?.status === filterStatus);
    }
    if (filterCategory !== 'all') {
      filtered = filtered?.filter(mapping => mapping?.categoryId === parseInt(filterCategory));
    }
    if (filterCity !== 'all') {
      filtered = filtered?.filter(mapping => mapping?.cityId === parseInt(filterCity));
    }

    setFilteredMappings(filtered);
    setCurrentPage(1);
  }, [mappings, categories, cities, filterStatus, filterCategory, filterCity]);

  const handleCreateMapping = () => {
    setSelectedMapping(null);
    setNewMapping({ categoryId: '', cityId: '', status: 'active' });
    setShowMappingModal(true);
  };

  const handleSaveMapping = () => {
    if (!newMapping?.categoryId || !newMapping?.cityId) {
      toast?.error('Please select both category and city');
      return;
    }

    // Check if mapping already exists
    const exists = mappings?.some(m => 
      m?.categoryId === parseInt(newMapping?.categoryId) && 
      m?.cityId === parseInt(newMapping?.cityId)
    );
    
    if (exists) {
      toast?.error('Mapping already exists');
      return;
    }

    const mappingData = {
      id: Date.now(),
      categoryId: parseInt(newMapping?.categoryId),
      cityId: parseInt(newMapping?.cityId),
      status: newMapping?.status,
      createdAt: new Date()?.toISOString()?.split('T')?.[0]
    };

    setMappings(prev => [...prev, mappingData]);
    setShowMappingModal(false);
    toast?.success('Mapping created successfully');
  };

  const handleToggleMapping = (mappingId) => {
    setMappings(prev => prev?.map(mapping => 
      mapping?.id === mappingId 
        ? { ...mapping, status: mapping?.status === 'active' ? 'inactive' : 'active' }
        : mapping
    ));
    toast?.success('Mapping status updated');
  };

  const handleDeleteMapping = (mappingId) => {
    if (window?.confirm('Are you sure you want to remove this category-city mapping?')) {
      setMappings(prev => prev?.filter(mapping => mapping?.id !== mappingId));
      toast?.success('Mapping removed successfully');
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredMappings?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMappings = filteredMappings?.slice(startIndex, startIndex + itemsPerPage);

  // Get unmapped combinations for bulk assignment
  const getUnmappedCities = (categoryId) => {
    const mappedCityIds = mappings?.filter(m => m?.categoryId === categoryId)?.map(m => m?.cityId);
    return cities?.filter(city => !mappedCityIds?.includes(city?.id) && city?.status === 'active');
  };

  // Add this function
  const handleBulkAssign = (categoryId) => {
    const unmappedCities = getUnmappedCities(categoryId);
    if (unmappedCities?.length === 0) {
      toast?.error('No unmapped cities available for this category');
      return;
    }
    
    const newMappings = unmappedCities?.map(city => ({
      id: Date.now() + Math.random(),
      categoryId: categoryId,
      cityId: city?.id,
      status: 'active',
      createdAt: new Date()?.toISOString()?.split('T')?.[0]
    }));
    
    setMappings(prev => [...prev, ...newMappings]);
    toast?.success(`Assigned category to ${unmappedCities?.length} cities`);
  };

  return (
    <div className="flex-1 overflow-hidden bg-background">
      <div className="flex flex-col h-full">
        {/* Header with Add Mapping button */}
        <div className="flex-shrink-0 bg-card border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Category-City Mapping</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Manage which categories are available in each city
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === 'grid' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Grid3x3" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === 'table' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>
              <button
                onClick={handleCreateMapping}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
              >
                <Icon name="Plus" size={16} />
                <span>Add Mapping</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Categories</option>
                {categories?.map(category => (
                  <option key={category?.id} value={category?.id}>{category?.name}</option>
                ))}
              </select>
              
              <select
                value={filterCity}
                onChange={(e) => setFilterCity(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Cities</option>
                {cities?.map(city => (
                  <option key={city?.id} value={city?.id}>{city?.name}, {city?.country}</option>
                ))}
              </select>
            </div>
            
            <div className="text-sm text-muted-foreground">
              {filteredMappings?.length} mappings found
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {paginatedMappings?.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="Network" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No mappings found</h3>
              <p className="text-muted-foreground mb-4">
                Start by assigning categories to cities.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedMappings?.map((mapping) => (
                <div key={mapping?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground">{mapping?.category?.name}</h3>
                      <p className="text-sm text-muted-foreground">{mapping?.city?.name}</p>
                      <p className="text-xs text-muted-foreground">{mapping?.city?.state}, {mapping?.city?.country}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      mapping?.status === 'active' ?'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                    }`}>
                      {mapping?.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Created: {new Date(mapping?.createdAt)?.toLocaleDateString()}
                    </span>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleToggleMapping(mapping?.id)}
                        className="p-1 hover:bg-muted rounded transition-colors"
                        title={`${mapping?.status === 'active' ? 'Deactivate' : 'Activate'} Mapping`}
                      >
                        <Icon 
                          name={mapping?.status === 'active' ? "EyeOff" : "Eye"} 
                          size={14} 
                          className="text-muted-foreground" 
                        />
                      </button>
                      <button
                        onClick={() => handleDeleteMapping(mapping?.id)}
                        className="p-1 hover:bg-destructive/10 rounded transition-colors"
                        title="Delete Mapping"
                      >
                        <Icon name="Trash2" size={14} className="text-destructive" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium text-foreground">Category</th>
                    <th className="text-left p-4 font-medium text-foreground">City</th>
                    <th className="text-left p-4 font-medium text-foreground">Country</th>
                    <th className="text-left p-4 font-medium text-foreground">Status</th>
                    <th className="text-left p-4 font-medium text-foreground">Created</th>
                    <th className="text-right p-4 font-medium text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMappings?.map((mapping) => (
                    <tr key={mapping?.id} className="border-t border-border">
                      <td className="p-4 text-foreground">{mapping?.category?.name}</td>
                      <td className="p-4 text-foreground">{mapping?.city?.name}</td>
                      <td className="p-4 text-muted-foreground">{mapping?.city?.country}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          mapping?.status === 'active' ?'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                        }`}>
                          {mapping?.status}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground text-sm">
                        {new Date(mapping?.createdAt)?.toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleToggleMapping(mapping?.id)}
                            className="p-2 hover:bg-muted rounded transition-colors"
                          >
                            <Icon 
                              name={mapping?.status === 'active' ? "EyeOff" : "Eye"} 
                              size={16} 
                              className="text-muted-foreground" 
                            />
                          </button>
                          <button
                            onClick={() => handleDeleteMapping(mapping?.id)}
                            className="p-2 hover:bg-destructive/10 rounded transition-colors"
                          >
                            <Icon name="Trash2" size={16} className="text-destructive" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredMappings?.length)} of {filteredMappings?.length}
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="px-4 py-2 text-sm">
                  {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Add Mapping Modal */}
      {showMappingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Create New Mapping
              </h3>
              <button
                onClick={() => setShowMappingModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={16} className="text-muted-foreground" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category *
                </label>
                <select
                  value={newMapping?.categoryId}
                  onChange={(e) => setNewMapping(prev => ({ ...prev, categoryId: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Category</option>
                  {categories?.filter(cat => cat?.status === 'active')?.map(category => (
                    <option key={category?.id} value={category?.id}>{category?.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  City *
                </label>
                <select
                  value={newMapping?.cityId}
                  onChange={(e) => setNewMapping(prev => ({ ...prev, cityId: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select City</option>
                  {cities?.filter(city => city?.status === 'active')?.map(city => (
                    <option key={city?.id} value={city?.id}>{city?.name}, {city?.country}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={newMapping?.status}
                  onChange={(e) => setNewMapping(prev => ({ ...prev, status: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowMappingModal(false)}
                className="px-4 py-2 border border-border rounded hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveMapping}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Create Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryCityMapping;